<template>
  <Dialog v-model:visible="visible" modal :header="$t('management.title')" class="insights-dialog insights-dialog--management" @hide="onHide">
    <template #default>
      <div class="modal-content">
        <DataTable class="insights-data-table" :value="data" paginator :rows="10" :rowsPerPageOptions="[10, 15, 20]" removableSort tableStyle="min-width: 50rem" :rowHover="true">
            <template #header>
                <div class="vlecad-table-toolbar">
                    <MultiSelect v-model="selectedColumnFields" :options="columns" optionLabel="header" optionValue="field" display="chip" :placeholder="$t('management.selectColumns')" />
                    <Button icon="pi pi-external-link" :label="$t('management.export')" class="p-button-outlined" @click="exportCSV($event)" />
                </div>
            </template>
            <Column :exportable="false" style="min-width: 8rem">
                <template #body="slotProps">
                    <Button outlined rounded class="mr-2 custom-button" :aria-label="$t('management.editRecord')" @click="editRecord(slotProps.data)"><i class="pi pi-pencil"></i></Button>
                    <Button outlined rounded severity="danger" class="custom-button" size="normal" :aria-label="$t('management.deleteRecord')" @click="deleteRecord(slotProps.data)"><i class="pi pi-trash"></i></Button>
                </template>
            </Column>
            <Column v-for="(col, index) in selectedColumns" :field="col.field" :header="col.header" :key="col.field + '_' + index" sortable style="text-align: center">
                <template #body="slotProps">
                    <span v-if="col.tooltip" v-tooltip="cellText(slotProps.data[col.field])">
                        {{ truncateCell(slotProps.data[col.field]) }}
                    </span>
                    <span v-else>
                        {{ slotProps.data[col.field] }}
                    </span>
                </template>
            </Column>
        </DataTable>
        <Dialog v-model:visible="deleteRecordDialog" class="insights-dialog--confirm" :header="$t('management.confirmTitle')" :modal="true">
            <div class="confirmation-message">
                <i class="pi pi-exclamation-triangle !text-3xl" />
                <span v-if="selectedRecord">{{ $t('management.confirmDelete', { id: selectedRecord.id, client: selectedRecord.client }) }}</span>
            </div>
            <template #footer>
                <Button :label="$t('management.no')" text @click="deleteRecordDialog = false"><i class="pi pi-times"></i></Button>
                <Button :label="$t('management.yes')" @click="deleteRecordCall(selectedRecord)"><i class="pi pi-check"></i></Button>
            </template>
        </Dialog>
        <span v-if="showError" class="error">{{ message }}</span>
        <Dialog v-model:visible="documentDetail" class="insights-dialog--form" :header="$t('management.documentDetails')" :modal="true">
            <div class="management-form">
                <div class="inputtext">
                    <label for="application" class="block font-bold mb-3">{{ $t('management.fields.application') }}:</label>
                    <InputText id="application" v-model="selectedRecord.application" fluid disabled />
                </div>
                <div class="inputtext">
                    <label for="customerid" class="block font-bold mb-3">{{ $t('management.fields.customerid') }}:</label>
                    <InputText id="customerid" v-model="selectedRecord.customerid" fluid disabled />
                </div>
                <div class="inputtext">
                    <label for="address" class="block font-bold mb-3">{{ $t('management.fields.address') }}:</label>
                    <InputText id="address" v-model="selectedRecord.address" fluid />
                </div>
                <div class="inputtext">
                    <label for="address2" class="block font-bold mb-3">{{ $t('management.fields.address2') }}:</label>
                    <InputText id="address2" v-model="selectedRecord.address2" fluid />
                </div>
                <div class="inputtext">
                    <label for="designer" class="block font-bold mb-3">{{ $t('management.fields.designer') }}:</label>
                    <InputText id="designer" v-model="selectedRecord.designer" fluid />
                </div>
                <div class="inputtext">
                    <label for="client" class="block font-bold mb-3">{{ $t('management.fields.client') }}:</label>
                    <InputText id="client" v-model="selectedRecord.client" fluid />
                </div>
                <div class="inputtext">
                    <label for="commission" class="block font-bold mb-3">{{ $t('management.fields.commission') }}:</label>
                    <InputText id="commission" v-model="selectedRecord.commission" fluid />
                </div>
                <div class="inputtext">
                    <label for="comment" class="block font-bold mb-3">{{ $t('management.fields.comment') }}:</label>
                    <InputText id="comment" v-model="selectedRecord.comment" fluid />
                </div>
                <div class="inputtext">
                    <label for="comment2" class="block font-bold mb-3">{{ $t('management.fields.comment2') }}:</label>
                    <InputText id="comment2" v-model="selectedRecord.comment2" fluid />
                </div>
                <div class="inputtext">
                    <label for="description" class="block font-bold mb-3">{{ $t('management.fields.description') }}:</label>
                    <InputText id="description" v-model="selectedRecord.description" fluid />
                </div>
                <div class="inputtext">
                    <label for="path" class="block font-bold mb-3">{{ $t('management.fields.path') }}:</label>
                    <InputText id="path" v-model="selectedRecord.path" fluid />
                </div>
                <div class="inputtext">
                    <label for="assemblydate" class="block font-bold mb-3">{{ $t('management.fields.assemblydate') }}:</label>
                    <InputText id="assemblydate" v-model="selectedRecord.assemblydate" fluid />
                </div>
                <div class="inputtext">
                    <label for="created" class="block font-bold mb-3">{{ $t('management.fields.created') }}:</label>
                    <InputText id="created" v-model="selectedRecord.created" fluid disabled />
                </div>
                <div class="inputtext">
                    <label for="deliverydate" class="block font-bold mb-3">{{ $t('management.fields.deliverydate') }}:</label>
                    <InputText id="deliverydate" v-model="selectedRecord.deliverydate" fluid />
                </div>
                <div class="inputtext">
                    <label for="productionstartdate" class="block font-bold mb-3">{{ $t('management.fields.productionstartdate') }}:</label>
                    <InputText id="productionstartdate" v-model="selectedRecord.productionstartdate" fluid />
                </div>
            </div>
            <template #footer>
                <Button :label="$t('management.cancel')" icon="pi pi-times" severity="secondary" @click="hideDialog" />
                <Button :label="$t('management.save')" icon="pi pi-check" @click="saveDocument(selectedRecord)" />
            </template>
        </Dialog>
      </div>
    </template>
  </Dialog>
</template>

<script>
import Dialog from 'primevue/dialog';
import { useAuth } from "@/store/auth";
import axios from "axios";
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import InputText from 'primevue/inputtext';
import Button from "primevue/button";
import MultiSelect from 'primevue/multiselect';

export default {
  components: { Dialog, DataTable, Column, InputText, Button, MultiSelect },
  props: {
    show: Boolean
  },
  data() {
    return {
      visible: this.show,
      data: [],
      deleteRecordDialog: false,
      selectedRecord: null,
      showError: false,
      errorKey: "",
      documentDetail: false,
      columnDefinitions: [{field: 'id', tooltip: false}, {field: 'customerid', tooltip: false}, {field: 'application', tooltip: false},
        {field: 'address', tooltip: true}, {field: 'address2', tooltip: true}, {field: 'designer', tooltip: true},
        {field: 'client', tooltip: true}, {field: 'commission', tooltip: false}, {field: 'comment', tooltip: true},
        {field: 'comment2', tooltip: true}, {field: 'description', tooltip: true}, {field: 'path', tooltip: true},
        {field: 'assemblydate', tooltip: false}, {field: 'created', tooltip: true}, {field: 'deliverydate', tooltip: false},
        {field: 'productionstartdate', tooltip: false}
      ],
      selectedColumnFields: ['application', 'address', 'designer', 'client', 'commission', 'comment', 'description', 'path', 'assemblydate', 'deliverydate', 'productionstartdate'],
    };
  },
  computed: {
    columns() {
      return this.columnDefinitions.map(column => ({
        ...column,
        header: this.$t(`management.fields.${column.field}`)
      }));
    },
    selectedColumns() {
      return this.columns.filter(column => this.selectedColumnFields.includes(column.field));
    },
    message() {
      return this.errorKey ? this.$t(`management.errors.${this.errorKey}`) : "";
    }
  },
  watch: {
    show(newValue) {
      this.visible = newValue;
      if(newValue) {
        this.getDocumentData();
      }
    }
  },
  methods: {
    cellText(value) {
      return value == null ? '' : String(value);
    },
    truncateCell(value) {
      const text = this.cellText(value);
      return text.length > 10 ? `${text.substring(0, 10)}...` : text;
    },
    exportCSV() {
      console.log("exportCSV");
      this.clearError();
      const now = new Date();
      const offset = now.getTimezoneOffset(); // Offset in minutes
      const localTime = new Date(now.getTime() - offset * 60000); // Convert UTC to local
      const formattedDate = localTime.toISOString().slice(0, 19).replace("T", "_").replace(/:/g, "-");
      const filename = `${this.$t('management.exportFilename')}_${formattedDate}`;
      console.log("filename: " + filename);

      if (!this.data || this.data.length === 0) {
        console.warn("No data available for export.");
        this.setError('noExportData');
        return;
      }

      var headers = [];
      var rows = [];
      // Define custom headers
      const exportFields = ['id', 'application', 'address', 'address2', 'designer', 'client', 'commission', 'comment', 'description', 'path', 'assemblydate', 'deliverydate', 'productionstartdate'];
      headers = exportFields.map(field => this.$t(`management.fields.${field}`)).join(",");

      // Map data to match the headers
      rows = this.data.map(row =>
        [
          `"${row.id}"`,
          `"${row.application}"`,
          `"${row.address}"`,
          `"${row.address2}"`,
          `"${row.designer}"`,
          `"${row.client}"`,
          `"${row.commission}"`,
          `"${row.comment}"`,
          `"${row.description}"`,
          `"${row.path}"`,
          `"${row.assemblydate}"`,
          `"${row.deliverydate}"`,
          `"${row.productionstartdate}"`
        ].join(",")
      );

      // Join headers and rows
      const csvContent = [headers, ...rows].join("\n");

      // Create a Blob and trigger the download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    async saveDocument(record) {
        console.log("saveDocument");
        this.clearError();
        console.log("application: " + this.selectedRecord.application);
        console.log("customerid: " + this.selectedRecord.customerid);
        const index = this.data.findIndex(item => item.id === this.selectedRecord.id);
        if (index !== -1) {
            const postData = {
                id: record.id,
                application: this.selectedRecord.application,
                customerid: this.selectedRecord.customerid,
                address: this.selectedRecord.address,
                address2: this.selectedRecord.address2,
                designer: this.selectedRecord.designer,
                client: this.selectedRecord.client,
                commission: this.selectedRecord.commission,
                comment: this.selectedRecord.comment,
                comment2: this.selectedRecord.comment2,
                description: this.selectedRecord.description,
                path: this.selectedRecord.path,
                assemblydate: this.selectedRecord.assemblydate,
                created: this.selectedRecord.created,
                deliverydate: this.selectedRecord.deliverydate,
                productionstartdate: this.selectedRecord.productionstartdate
            };
            await axios.post(this.baseUrl+"api/updateDocuments", postData)
            .then((res) => {
                console.log("updateDocuments res: " + JSON.stringify(res));
                this.data[index] = { ...this.selectedRecord };
            })
            .catch((error) => {
                console.log("updateDocuments error: " + JSON.stringify(error));
                if (error.response) {
                    // Server responded with a status code outside the 2xx range
                    console.error("Error:", error.response.data.message); 
                    this.setError('saveFailed');
                } 
                else if (error.request) {
                    // No response received (network error)
                    console.error("No response from server");
                    this.setError('network');
                } 
                else {
                    // Something else happened
                    console.error("Unexpected error:", error.message);
                    this.setError('saveFailed');
                }
            });
        }
        this.documentDetail = false; // Close the edit dialog
    },
    hideDialog() {
        console.log("hideDialog");
        this.documentDetail = false;
    },
    editRecord(record) {
        console.log("editRecord");
        this.selectedRecord = JSON.parse(JSON.stringify(record));
        this.documentDetail = true;
    },
    deleteRecord(record) {
        console.log("deleteRecord");
        console.log("record: " + JSON.stringify(record));
        this.selectedRecord = record;
        this.deleteRecordDialog = true;
        this.clearError();
    },
    async deleteRecordCall(record) {
        console.log("deleteRecordCall");
        console.log("id: " + JSON.stringify(record.id));
        await axios.delete(this.baseUrl+"api/deleteAllDocumentsById/"+record.id)
        .then((res) => {
            console.log("deleteRecordCall res: " + JSON.stringify(res));
            //this.data = res.data;
            const index = this.data.findIndex(item => item.id === record.id);
            console.log("index: " + index);
            if(index !== -1) {
                this.data.splice(index, 1);
            }
        })
        .catch((error) => {
            console.log("deleteRecordCall error: " + JSON.stringify(error));
            if (error.response) {
                // Server responded with a status code outside the 2xx range
                console.error("Error:", error.response.data.message); 
                this.setError('deleteFailed');
            } 
            else if (error.request) {
                // No response received (network error)
                console.error("No response from server");
                this.setError('network');
            } 
            else {
                // Something else happened
                console.error("Unexpected error:", error.message);
                this.setError('deleteFailed');
            }
        });
        //this.getDocumentData();
        this.deleteRecordDialog = false;
    },
    closeModal() {
      this.visible = false;
      this.$emit('update:show', false); // Update de parent component
    },
    onHide() {
        this.$emit('update:show', false); // Update de parent component
    },
    setError(key) {
        this.errorKey = key;
        this.showError = true;
    },
    clearError() {
        this.errorKey = "";
        this.showError = false;
    },
    async getDocumentData() {
        console.log("getDocumentData");
        this.clearError();
        const { authState } = useAuth();
        console.log("customerId: " + authState.customerId);
        await axios.get(this.baseUrl+"api/getAllDocumentsByCustomerId/"+authState.customerId+"/"+"")
        .then((res) => {
            console.log("res: " + JSON.stringify(res));
            this.data = res.data;
        })
        .catch((error) => {
            console.log("error: " + JSON.stringify(error));
            this.setError(error.request ? 'network' : 'loadFailed');
        });
    }
  }
};
</script>

<style scoped>
.modal-content {
  width: 100%;
  padding: 0.25rem;
}

.custom-button {
  width: 2.5rem;
  height: 2.5rem;
  min-height: 2.5rem;
  margin: 0.15rem;
  padding: 0;
}

.error {
  display: block;
  margin-top: 1rem;
  padding: 0.75rem 1rem;
  color: #ffffff;
  border-radius: var(--radius-small);
  background: var(--color-error);
}

.management-form {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem 1.25rem;
}

.inputtext {
  min-width: 0;
}

.inputtext label {
  display: block;
  margin-bottom: 0.35rem;
  color: var(--color-heading);
  font-weight: 600;
}

.confirmation-message {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  line-height: 1.5;
}

.confirmation-message .pi {
  flex: 0 0 auto;
  margin-top: 0.15rem;
  color: var(--color-gold);
  font-size: 1.75rem;
}

@media (max-width: 48rem) {
  .management-form {
    grid-template-columns: 1fr;
  }
}
</style>
